import { ActivityItem } from "@/store/zustand/activityStore";
import { Message } from "@/store/zustand/supabaseStore";
import { parseMessageContentBlocks } from "./messages";

/**
 * Extracts activity IDs from a message's tool call results
 */
export const extractActivityIdsFromMessage = (message: Message): string[] => {
  const segments = parseMessageContentBlocks(message);
  return segments
    .filter(
      (s) =>
        s.toolCallName === "applyFileEditsTool" && s.toolCallResult?.activityId,
    )
    .map((s) => s.toolCallResult.activityId);
};

/**
 * Finds the latest activity from a list of activity IDs
 * Also detects if any activities have been overwritten (deleted from stream)
 */
export const findLatestActivity = (
  activityIds: string[],
  activityStreamLookup: Record<string, ActivityItem>,
): { activity: ActivityItem | null; hasOverwrittenActivities: boolean } => {
  let foundActivity: ActivityItem | undefined;
  let latestTimestamp = "";
  let hasOverwrittenActivities = false;

  activityIds.forEach((id) => {
    const activity = activityStreamLookup[id];
    if (activity && activity.timestamp > latestTimestamp) {
      latestTimestamp = activity.timestamp;
      foundActivity = activity;
    } else if (!activity) {
      hasOverwrittenActivities = true;
    }
  });

  return {
    activity: foundActivity || null,
    hasOverwrittenActivities,
  };
};

/**
 * Finds the current HEAD activity (the latest non-undone activity)
 */
export const findCurrentHeadActivity = (
  activityStreamList: ActivityItem[],
): ActivityItem | undefined => {
  return activityStreamList.find((activity) => !activity.change_undone);
};

/**
 * Checks if the given activity is the current HEAD
 */
export const checkIfCurrentHead = (
  activity: ActivityItem | null,
  activityStreamList: ActivityItem[],
): boolean => {
  if (!activity) return false;
  const currentActivity = findCurrentHeadActivity(activityStreamList);
  return currentActivity?.id === activity.id;
};

/**
 * Checks if an activity or any related activities have been overwritten
 */
export const checkIfOverwritten = (
  activity: ActivityItem | null,
  hasOverwrittenActivities: boolean,
  activityStreamLookup: Record<string, ActivityItem>,
): boolean => {
  if (!activity) return hasOverwrittenActivities;
  return !activityStreamLookup[activity.id] || hasOverwrittenActivities;
};
